/**
 * The Registry station tab (M5 spec §12.4, §15.5, §18 flow 2; plan C18): at Authority stations it lists the bounties
 * on the player per faction with their wanted levels, and "Pay fines" clears **every** bounty for `Σ bounty × 1.5`
 * through the Career wallet (C14's `payCharterFines`). A `StationServicePanel`, so it mounts in M2's station screen
 * (through the M3 bridge) and M3's services panel alike. UI events only.
 */
import type { ReactNode } from 'react'
import lawJson from '../../data/strategic/law.json'
import type { PayFinesResult } from '../../charter/fines'
import { factionShort } from '../../charter/tolls'
import type { StationSite } from '../../galaxy/layout/types'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import { FACTION_IDS, type FactionId, type RelationsView } from '../../strategic/types'
import { mountReact } from '../strategic/mount'

export interface RegistryRow {
  faction: FactionId
  name: string
  bounty: string
  wanted: number
}

export interface RegistryModel {
  rows: RegistryRow[]
  /** What paying costs (`mult × Σ bounty`). */
  total: number
  totalLabel: string
  /** Something is owed and the wallet covers it. */
  canPay: boolean
  /** Why paying is off ('' when it is on). */
  hint: string
}

function credits(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

/** The Registry's rows and fine from the mirrored relations and the wallet. */
export function registryModel(view: RelationsView | null, wallet: number, mult: number = lawJson.fines.mult): RegistryModel {
  const rows: RegistryRow[] = []
  let sum = 0
  if (view !== null) {
    for (const id of FACTION_IDS) {
      const b = view.player.bounties[id] ?? 0
      if (!(b > 0)) continue
      sum += b
      rows.push({ faction: id, name: factionShort(id), bounty: credits(b), wanted: view.player.wanted[id] ?? 0 })
    }
  }
  const total = Math.round(sum * mult)
  const owed = total > 0
  const covered = wallet >= total
  return {
    rows,
    total,
    totalLabel: credits(total),
    canPay: owed && covered,
    hint: !owed ? 'No bounties on record.' : covered ? '' : `Not enough credits: fines are ${credits(total)}.`,
  }
}

/** The line after a payment attempt. */
export function payResultText(r: PayFinesResult): string {
  switch (r.status) {
    case 'paid':
      return `Fines paid: ${credits(r.amount)}. Every bounty is cleared.`
    case 'nothingOwed':
      return 'No bounties on record.'
    case 'notEnoughCredits':
      return `Not enough credits: fines are ${credits(r.amount)}.`
    case 'failed':
      return `The Registry could not take the payment (${r.error}).`
  }
}

export function RegistryPanel(p: { model: RegistryModel; status: string | null; busy: boolean; onPay: () => void }): ReactNode {
  const m = p.model
  return (
    <section className="ch-registry" data-testid="charter-registry">
      <h2 className="ch-registry__title">Charter Registry: bounties</h2>
      {m.rows.length === 0 ? (
        <p className="ch-registry__empty">No bounties on record.</p>
      ) : (
        <table className="ch-registry__table">
          <thead>
            <tr>
              <th scope="col">Faction</th>
              <th scope="col">Bounty</th>
              <th scope="col">Wanted</th>
            </tr>
          </thead>
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.faction} data-testid={`registry-row-${r.faction}`}>
                <td>{r.name}</td>
                <td className="ui-num">{r.bounty}</td>
                <td>{r.wanted > 0 ? `WANTED ${r.wanted}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="ch-registry__total">
        Fines (bounties × {lawJson.fines.mult}): <span className="ui-num">{m.totalLabel}</span>
      </p>
      <button type="button" className="ch-registry__pay" data-testid="registry-pay" disabled={!m.canPay || p.busy} onClick={p.onPay}>
        Pay fines
      </button>
      {m.hint !== '' ? <p className="ch-registry__hint">{m.hint}</p> : null}
      {p.status !== null ? (
        <p className="ch-registry__status" role="status" data-testid="registry-status">
          {p.status}
        </p>
      ) : null}
    </section>
  )
}

/** What the Registry tab reads and does (the Charter HUD feature binds it). */
export interface RegistryPanelDeps {
  relations(): RelationsView | null
  onRelations(fn: (view: RelationsView) => void): () => void
  wallet(): number
  pay(): Promise<PayFinesResult>
}

/** After Parts (40), the Station (50) and Wire (60) tabs; before Home (90). */
export const REGISTRY_PANEL_ORDER = 70

/** The Registry is at stations of the fines faction (law.json: the Authority). */
export function registryAvailable(site: Pick<StationSite, 'faction'>): boolean {
  return site.faction === lawJson.fines.faction
}

export function createRegistryPanel(deps: RegistryPanelDeps): StationServicePanel {
  return {
    id: 'registry',
    title: 'Registry',
    order: REGISTRY_PANEL_ORDER,
    available: registryAvailable,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const mount = mountReact(el, 'sui-mount')
      let status: string | null = null
      let busy = false
      let alive = true
      const render = (): void => {
        if (!alive) return
        mount.render(<RegistryPanel model={registryModel(deps.relations(), deps.wallet())} status={status} busy={busy} onPay={pay} />)
        ctx.refresh()
      }
      function pay(): void {
        if (busy) return
        busy = true
        render()
        void deps
          .pay()
          .catch((err: unknown): PayFinesResult => ({ status: 'failed', error: err instanceof Error ? err.message : String(err) }))
          .then((r) => {
            busy = false
            status = payResultText(r)
            render()
          })
      }
      render()
      const off = deps.onRelations(render)
      return (): void => {
        alive = false
        off()
        mount.unmount()
      }
    },
  }
}
